import { useMemo, useState } from "react";
import { Search, Trash2, UserPlus } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import {
  useGrantOrderAccess,
  useOrderAccessGrants,
  useRevokeOrderAccessGrant,
  useUpdateOrderAccessGrant,
} from "../hooks/useOrders";
import { ORDER_ACCESS_TYPE_HINTS, ORDER_ACCESS_TYPE_LABELS, type OrderAccessGrant, type OrderAccessType } from "../types";

const ACCESS_TYPES: OrderAccessType[] = ["view", "manage", "execute"];

const ACCESS_BADGE: Record<OrderAccessType, "gray" | "blue" | "orange"> = {
  view: "gray",
  manage: "blue",
  execute: "orange",
};

/** DRF error payloads come as a string, a list, or nested {field: [msgs]} — one line either way. */
function flattenErrors(errors: unknown): string {
  if (!errors) return "";
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.map(flattenErrors).filter(Boolean).join(" ");
  if (typeof errors === "object") return Object.values(errors as Record<string, unknown>).map(flattenErrors).filter(Boolean).join(" ");
  return String(errors);
}

function errorMessage(err: unknown, fallback: string): string {
  const res = (err as { response?: { data?: { errors?: unknown } } } | undefined)?.response;
  return flattenErrors(res?.data?.errors) || fallback;
}

interface Props {
  orderId: string;
  /** `order.can_grant_access` — zamawiający, dyrektor techniczny, prezes. */
  canGrant: boolean;
}

/**
 * „Daj dostęp pracownikowi GK Elektim” — sekcja na karcie zamówienia dla zamawiającego
 * i dyrektorów technicznych. Lista nadanych dostępów (zmiana typu, odebranie) +
 * formularz nadania: przewijana lista pracowników, którzy zamówienia jeszcze nie widzą
 * (bez prezesa i dyrektorów — oni mają dostęp z roli), i typ dostępu.
 * Powiadomień tu nie ma: obdarowany z „Zarządzaniem” włącza je sobie sam, osobno,
 * istniejącym przyciskiem „Włącz powiadomienia” na karcie zamówienia.
 */
export function OrderAccessGrantsSection({ orderId, canGrant }: Props) {
  const { data, isLoading } = useOrderAccessGrants(orderId, canGrant);
  const update = useUpdateOrderAccessGrant(orderId);
  const revoke = useRevokeOrderAccessGrant(orderId);
  const [modalOpen, setModalOpen] = useState(false);
  const [rowError, setRowError] = useState("");

  if (!canGrant) return null;
  const grants = data?.grants ?? [];

  const changeType = async (grant: OrderAccessGrant, access_type: OrderAccessType) => {
    setRowError("");
    try {
      await update.mutateAsync({ grantId: grant.id, access_type });
    } catch (err) {
      setRowError(errorMessage(err, "Nie udało się zmienić typu dostępu."));
    }
  };

  const remove = async (grant: OrderAccessGrant) => {
    if (!window.confirm(`Odebrać dostęp: ${grant.user_name}?`)) return;
    setRowError("");
    try {
      await revoke.mutateAsync(grant.id);
    } catch (err) {
      setRowError(errorMessage(err, "Nie udało się odebrać dostępu."));
    }
  };

  return (
    <div className="mt-6 bg-white dark:bg-gray-800 p-4" data-testid="order-access-grants">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <UserPlus size={16} />
          Dostęp pracowników GK ELEKTIM
        </h2>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          <UserPlus size={14} /> Daj dostęp pracownikowi GK Elektim
        </Button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Prezes, dyrektorzy, księgowość, kadry i koordynatorzy przedsięwzięcia widzą zamówienie z racji roli.
        Tu nadajesz dostęp pozostałym pracownikom: Podgląd (bez kwot i akcji), Zarządzanie (jak zamawiający)
        albo Realizacja (widok jak u podwykonawcy, bez kwot, wniosków o zmianę i POCK).
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Ładowanie…</p>
      ) : grants.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Nikt spoza ról nie ma jeszcze dostępu do tego zamówienia.</p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {grants.map((grant) => (
            <li key={grant.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                  {grant.user_name}
                  <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500">{grant.user_role_display}</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Nadał(a): {grant.granted_by_name ?? "—"} · {new Date(grant.created_at).toLocaleDateString("pl")}
                </p>
              </div>
              <Badge color={ACCESS_BADGE[grant.access_type]}>{grant.access_type_display}</Badge>
              <select
                aria-label={`Typ dostępu: ${grant.user_name}`}
                value={grant.access_type}
                onChange={(e) => changeType(grant, e.target.value as OrderAccessType)}
                disabled={update.isPending}
                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-2 py-1 text-xs"
              >
                {ACCESS_TYPES.map((t) => (
                  <option key={t} value={t}>{ORDER_ACCESS_TYPE_LABELS[t]}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => remove(grant)}
                disabled={revoke.isPending}
                className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                title="Odbierz dostęp"
              >
                <Trash2 size={13} /> Odbierz
              </button>
            </li>
          ))}
        </ul>
      )}
      {rowError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{rowError}</p>}

      <GrantAccessModal
        orderId={orderId}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        candidates={data?.candidates ?? []}
      />
    </div>
  );
}

interface ModalProps {
  orderId: string;
  open: boolean;
  onClose: () => void;
  candidates: { id: string; full_name: string; role_display: string; job_title: string }[];
}

/**
 * Formularz nadania dostępu: przewijana lista pracowników bez dostępu (wielokrotny
 * wybór — osób może być więcej) i typ dostępu. Kandydaci to wyłącznie role pracownik
 * i koordynator (backend: `grant_candidates`; księgowość, kadry, dyrektorzy i prezes
 * widzą zamówienia z roli, więc nie ma ich tu). Lista pokazuje same nazwiska — bez
 * roli i stanowiska.
 */
function GrantAccessModal({ orderId, open, onClose, candidates }: ModalProps) {
  const grant = useGrantOrderAccess(orderId);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [accessType, setAccessType] = useState<OrderAccessType>("view");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => c.full_name.toLowerCase().includes(q));
  }, [candidates, query]);

  const reset = () => {
    setQuery("");
    setSelected(new Set());
    setAccessType("view");
    setError("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const submit = async () => {
    if (selected.size === 0) {
      setError("Wybierz co najmniej jednego pracownika.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      for (const userId of selected) {
        await grant.mutateAsync({ user: userId, access_type: accessType });
      }
      close();
    } catch (err) {
      setError(errorMessage(err, "Nie udało się nadać dostępu."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={close} title="Daj dostęp pracownikowi GK Elektim" size="lg">
      <div className="flex flex-col gap-4">
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Pracownicy bez dostępu do tego zamówienia
          </label>
          <div className="relative mb-2">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj po nazwisku…"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 pl-7 pr-3 py-1.5 text-sm"
            />
          </div>
          {/* Przewijana lista — pracowników bywa kilkudziesięciu; formularz nie może rosnąć z nimi. */}
          <div className="max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700" data-testid="order-access-candidates">
            {filtered.length === 0 ? (
              <p className="px-3 py-4 text-sm text-gray-400 dark:text-gray-500">
                {candidates.length === 0
                  ? "Wszyscy pracownicy, którym da się nadać dostęp, już go mają."
                  : "Brak pracowników pasujących do wyszukiwania."}
              </p>
            ) : (
              filtered.map((c) => (
                <label key={c.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                    className="rounded"
                  />
                  <span className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100">{c.full_name}</span>
                </label>
              ))
            )}
          </div>
          {selected.size > 0 && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Wybrano: {selected.size}</p>
          )}
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Typ dostępu</legend>
          <div className="flex flex-col gap-2">
            {ACCESS_TYPES.map((t) => (
              <label key={t} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="order-access-type"
                  value={t}
                  checked={accessType === t}
                  onChange={() => setAccessType(t)}
                  className="mt-0.5"
                />
                <span>
                  <span className="block text-sm text-gray-900 dark:text-gray-100">{ORDER_ACCESS_TYPE_LABELS[t]}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">{ORDER_ACCESS_TYPE_HINTS[t]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={close} disabled={saving}>Anuluj</Button>
          <Button size="sm" onClick={submit} loading={saving} disabled={selected.size === 0}>
            Nadaj dostęp
          </Button>
        </div>
      </div>
    </Modal>
  );
}
